// The comparison Distribution view (04h-5d) — the overlay counterpart to RadarDistribution,
// ported from renderers-radar.js's renderRadarDistributionOverlay / distRowHtmlMulti. Same real
// per-axis population curve as the single-player Distribution, but with ONE marker per merged
// player (coloured to match their legend chip) instead of a two-tone split — a two-tone split only
// ever meant something relative to ONE player's value (renderers-radar.js:884).
//
// Plain React JSX over the 04h-5a pure layer (no d3). Renders into the shared .radar-table-wrap
// slot when a merged module is in Distribution view (the wheel's SVG is hidden), exactly like the
// single-player Distribution shares that slot with the table.

import { Fragment, useMemo } from "react";
import {
  densityCurve,
  distSingleAreaPath,
  distMarkerY,
  percentileRank,
  truncateLabel,
  AXIS_LABEL_MAX_LEN,
  DIST_ROW_W,
  DIST_ROW_H,
  DIST_BASELINE_Y,
  type DistSpread,
  type RadarAxis,
} from "./radar-view.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";

interface Props {
  /** Who each row is drawn against (radar-view.ts playerSpread; a team's, teamSpread). */
  spread: DistSpread;
  /** The chart axes (no Minutes). */
  axes: readonly RadarAxis[];
  /** Each marker's name on hover, one per player or team (a player's last name). */
  names: readonly string[];
  /** Per player, their value per axis key (a superset map — only chart axes are read). */
  valuesList: readonly Record<string, number | null | undefined>[];
  /** Per player, their colour letter (index-matched). */
  letters: readonly string[];
}

interface OverlayMarker {
  letter: string;
  xPct: number;
  yPct: number;
  tooltip: string;
}
interface OverlayValueCol {
  letter: string;
  valueText: string;
  pctile: number | null;
}
interface OverlayRow {
  key: string;
  shortLabel: string;
  labelTooltip: string | undefined;
  hasPoints: boolean;
  singleArea: string;
  markers: OverlayMarker[];
  valueCols: OverlayValueCol[] | null;
  leftTick: string;
  midTick: string;
  rightTick: string;
}

// A merged-in player's marker position (0–1), clamped then mirrored for lower-is-better so the good
// end stays on the right for the comparison row too (renderers-radar.js:947).
function overlayMarkerFrac(rawValue: number, domainMin: number, domainMax: number, lowerBetter: boolean): number {
  const rawFrac = domainMax > domainMin ? Math.max(0, Math.min(1, (rawValue - domainMin) / (domainMax - domainMin))) : 0.5;
  return lowerBetter ? 1 - rawFrac : rawFrac;
}

export function RadarDistributionOverlay({ spread, axes, names, valuesList, letters }: Props) {
  // Per-player Value/%ile columns only read cleanly for 2 (past that it's "just show the graphs",
  // the same threshold Stats' comparison uses). renderers-radar.js:957.
  const showValues = names.length <= 2;

  const rows = useMemo<OverlayRow[]>(() => {
    return axes.map((axis) => {
      const popValues = spread.valuesOf(axis.key);
      const domainMin = popValues.length ? popValues[0]! : 0;
      const domainMax = popValues.length ? popValues[popValues.length - 1]! : 0;
      const lowerBetter = spread.lowerBetter(axis.key);
      const points = densityCurve(popValues, lowerBetter);

      const markers: OverlayMarker[] = [];
      const valueCols: OverlayValueCol[] = [];
      names.forEach((name, i) => {
        const rawValue = valuesList[i]?.[axis.key];
        const hasValue = rawValue != null && !Number.isNaN(rawValue);
        if (hasValue) {
          const frac = overlayMarkerFrac(rawValue as number, domainMin, domainMax, lowerBetter);
          markers.push({
            letter: letters[i]!,
            xPct: frac * 100,
            yPct: (distMarkerY(points, frac) / DIST_ROW_H) * 100,
            tooltip: `${name}: ${formatMetricValue(rawValue, axis.outputType)}`,
          });
        }
        valueCols.push({
          letter: letters[i]!,
          valueText: formatMetricValue(rawValue, axis.outputType),
          pctile: hasValue ? percentileRank(popValues, rawValue as number) : null,
        });
      });

      const midValue = domainMin + (domainMax - domainMin) / 2;
      const leftDomainValue = lowerBetter ? domainMax : domainMin;
      const rightDomainValue = lowerBetter ? domainMin : domainMax;

      return {
        key: axis.key,
        shortLabel: truncateLabel(axis.label, AXIS_LABEL_MAX_LEN),
        labelTooltip: axis.label.length > AXIS_LABEL_MAX_LEN ? axis.label : undefined,
        hasPoints: points.length > 0,
        singleArea: points.length ? distSingleAreaPath(points) : "",
        markers,
        valueCols: showValues ? valueCols : null,
        leftTick: formatMetricValue(leftDomainValue, axis.outputType),
        midTick: formatMetricValue(midValue, axis.outputType),
        rightTick: formatMetricValue(rightDomainValue, axis.outputType),
      };
    });
  }, [spread, axes, names, valuesList, letters, showValues]);

  return (
    <>
      <div className="radar-dist-header">
        <span className="radar-dist-header-title">Distribution</span>
        <span className="radar-dist-header-sub">{spread.sub}</span>
      </div>
      {showValues && (
        <div className="radar-dist-row radar-dist-col-headers">
          <span className="radar-dist-label" />
          <span className="radar-dist-chart-wrap" />
          {names.map((_, i) => (
            <Fragment key={i}>
              <span className="radar-dist-value">Value</span>
              <span className="radar-dist-pctile">%ile</span>
            </Fragment>
          ))}
        </div>
      )}
      <div className="radar-dist-rows">
        {rows.map((r) => (
          <div className="radar-dist-row" data-key={r.key} key={r.key}>
            <span className="radar-dist-label" {...(r.labelTooltip ? { "data-tooltip": r.labelTooltip } : {})}>
              {r.shortLabel}
            </span>
            <div className="radar-dist-chart-wrap">
              <div className="radar-dist-svg-wrap">
                <svg className="radar-dist-svg" viewBox={`0 0 ${DIST_ROW_W} ${DIST_ROW_H}`} preserveAspectRatio="none">
                  {r.hasPoints && (
                    <>
                      <line className="radar-dist-baseline" x1="0" y1={DIST_BASELINE_Y} x2={DIST_ROW_W} y2={DIST_BASELINE_Y} />
                      <path className="radar-dist-area radar-dist-area-above" d={r.singleArea} />
                    </>
                  )}
                </svg>
                {r.markers.map((m, i) => (
                  <div
                    key={i}
                    className={`radar-dist-marker-tri radar-dist-marker-${m.letter}`}
                    style={{ left: `${m.xPct.toFixed(2)}%`, top: `${m.yPct.toFixed(2)}%` }}
                    data-tooltip={m.tooltip}
                  />
                ))}
              </div>
              <div className="radar-dist-ticks">
                <span className="radar-dist-tick" style={{ left: "0%" }}>
                  {r.leftTick}
                </span>
                <span className="radar-dist-tick" style={{ left: "50%" }}>
                  {r.midTick}
                </span>
                <span className="radar-dist-tick radar-dist-tick-end" style={{ left: "100%" }}>
                  {r.rightTick}
                </span>
              </div>
            </div>
            {r.valueCols?.map((c, i) => (
              <Fragment key={i}>
                <span className={`radar-dist-value radar-dist-value-${c.letter}`}>{c.valueText}</span>
                <span className={`radar-dist-pctile radar-dist-pctile-${c.letter}`}>{c.pctile === null ? "—" : c.pctile}</span>
              </Fragment>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
